import { VenueOpeningHours, VenueOpeningHoursPeriod } from '@ivy-monorepo/shared'

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

export interface DayScheduleRow {
  day: number
  /** When true, no open/close times are stored for this day (not the same as "venue is shut down"). */
  hoursNotSet: boolean
  open24Hours: boolean
  openTime: string
  closeTime: string
}

export function defaultDayScheduleRows(): DayScheduleRow[] {
  return WEEKDAY_LABELS.map((_, day) => ({
    day,
    hoursNotSet: true,
    open24Hours: false,
    openTime: '09:00',
    closeTime: '17:00',
  }))
}

function parseTimeString(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) {
    return null
  }

  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null
  }

  return { hour, minute }
}

function formatTimeString(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function formatTime12h(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  const minutePart = minute > 0 ? `:${String(minute).padStart(2, '0')}` : ''
  return `${hour12}${minutePart} ${period}`
}

function applyPeriodToRow(row: DayScheduleRow, period: VenueOpeningHoursPeriod): void {
  row.hoursNotSet = false
  row.openTime = formatTimeString(period.open.hour, period.open.minute)

  if (!period.close) {
    row.open24Hours = true
    row.closeTime = '23:59'
    return
  }

  row.open24Hours = false
  row.closeTime = formatTimeString(period.close.hour, period.close.minute)
}

export function dayScheduleRowsFromOpeningHours(hours: VenueOpeningHours | null | undefined): DayScheduleRow[] {
  const rows = defaultDayScheduleRows()

  if (!hours?.periods?.length) {
    return rows
  }

  for (const period of hours.periods) {
    const row = rows.find(r => r.day === period.open.day)
    if (row) {
      applyPeriodToRow(row, period)
    }
  }

  return rows
}

export function openingHoursFromDayScheduleRows(rows: DayScheduleRow[]): VenueOpeningHours | null {
  const periods: VenueOpeningHoursPeriod[] = []
  const weekdayDescriptions: string[] = []

  for (const row of rows) {
    if (row.hoursNotSet) {
      continue
    }

    const dayLabel = WEEKDAY_LABELS[row.day]

    if (row.open24Hours) {
      periods.push({
        open: { day: row.day, hour: 0, minute: 0 },
      })
      weekdayDescriptions.push(`${dayLabel}: Open 24 hours`)
      continue
    }

    const open = parseTimeString(row.openTime)
    const close = parseTimeString(row.closeTime)

    if (!open || !close) {
      throw new Error(`Invalid time for ${dayLabel}`)
    }

    periods.push({
      open: { day: row.day, hour: open.hour, minute: open.minute },
      close: { day: row.day, hour: close.hour, minute: close.minute },
    })
    weekdayDescriptions.push(
      `${dayLabel}: ${formatTime12h(open.hour, open.minute)} – ${formatTime12h(close.hour, close.minute)}`
    )
  }

  if (periods.length === 0) {
    return { periods: [], weekdayDescriptions: ['Hours not set'] }
  }

  return { periods, weekdayDescriptions }
}

export function formatDayScheduleRow(row: DayScheduleRow): string {
  if (row.hoursNotSet) {
    return 'Not set'
  }
  if (row.open24Hours) {
    return '24 hours'
  }
  return `${row.openTime}–${row.closeTime}`
}
